import { useEffect, useState } from 'react'
import { isEmpty } from 'lodash'
import moment from 'moment'
import { TableCell, TableRow } from '@mui/material'
import Content from 'components/content/content'
import CONSTANT from 'modules/constant'
import GLOBAL from 'modules/global'
import type { ErrorProps } from 'modules/types'
import type { TradeInHistory, PartialTradeInHistory } from 'modules/tradein/trade-in.dto'
import Notification from 'components/notification'
import { useDownloadTradeInListMutation, useGetTradeInListMutation } from 'store/tradeIn'

const TradeInList = () => {
  const [getListSubmissions, submissionsList] = useGetTradeInListMutation()
  const [downloadTradeInList, download] = useDownloadTradeInListMutation()

  const [payload, setPayload] = useState<PartialTradeInHistory>({
    start: 0,
    length: CONSTANT.DEFAULT_PAGINATION_PER_PAGE,
    search: ''
  })

  const formatDate = (e: string) => (!isEmpty(e) ? moment(e).format('DD-MM-YYYY HH:mm') : '-')
  const onSearch = (value: string) => setPayload({ ...payload, start: 0, search: value })
  const onChangePage = (value: number) => setPayload({ ...payload, start: value - 1 })
  const onChangePerPage = (value: number) => setPayload({ ...payload, length: value })

  const getField = () =>
    columns
      .filter((c) => c.searchable !== false)
      .map((c) => c.key)
      .join(',')

  const onResync = () => {
    const formData = new FormData()

    formData.append('start', payload.start.toString())
    formData.append('length', payload.length.toString())
    formData.append('field', getField())

    if (!isEmpty(payload.search)) formData.append('search[value]', payload.search)

    getListSubmissions(formData)
  }

  const onDownload = (type?: string) => {
    const formData = new FormData()

    formData.append('start', String(0))
    formData.append('length', CONSTANT.DEFAULT_PAGINATION_MAXIMUM_DOWNLOAD.toString())
    formData.append('field', getField())

    if (!isEmpty(payload.search)) formData.append('search[value]', payload.search)
    if (type && !isEmpty(type)) formData.append('Download-Type', type)

    downloadTradeInList(formData)
  }

  const columns = [
    {
      column: 'Member Code',
      label: 'Member Code',
      searchable: true,
      key: 'memberCode'
    },
    {
      column: 'Full Name',
      label: 'Full Name',
      searchable: true,
      key: 'fullName'
    },
    {
      column: 'Phone Number',
      label: 'Phone Number',
      searchable: true,
      key: 'phoneNumber'
    },
    {
      column: 'Vehicle Brand',
      label: 'Vehicle Brand',
      searchable: true,
      key: 'carBrand',
      render: (row: TradeInHistory) => (row.vin ? 'Hyundai' : 'Non-Hyundai')
    },
    {
      column: 'Brand (Non-Hyundai)',
      label: 'Brand (Non-Hyundai)',
      searchable: false,
      render: (row: TradeInHistory) => (row.vin ? '-' : row.carBrand || '-')
    },
    {
      column: 'VIN',
      label: 'VIN',
      searchable: true,
      key: 'vin'
    },
    {
      column: 'Car Model',
      label: 'Car Model',
      searchable: true,
      key: 'carModelName'
    },
    {
      column: 'Model Year',
      label: 'Model Year',
      searchable: true,
      key: 'carModelYear'
    },
    {
      column: 'Hyundai Preferred Model',
      label: 'Hyundai Preferred Model',
      searchable: true,
      key: 'preferredHyundaiModel'
    },
    {
      column: 'Form Submission Date',
      label: 'Form Submission Date',
      key: 'formSubmissionDate',
      searchable: false,
      render: (row: TradeInHistory) => formatDate(row.formSubmissionDate)
    }
  ]

  const tableHeadTitles = columns.map((c) => c.label)

  const totalPage = submissionsList.data && GLOBAL.tableTotalPage(submissionsList.data.recordsTotal)
  const numbers = GLOBAL.tableNumber(payload.start)

  useEffect(() => {
    onResync()
  }, [payload])

  return (
    <>
      <Content
        title='Trade-In Submissions'
        tableHeadTitles={tableHeadTitles}
        onSearch={onSearch}
        onResync={onResync}
        onDownload={onDownload}
        downloadLoading={download.isLoading}
        onDownloadTypeIgnored={['pdf']}
        pagination={onChangePage}
        onChangePerPage={onChangePerPage}
        totalPage={totalPage}
        totalRecords={submissionsList.data && submissionsList.data.recordsTotal}
        page={payload.start + 1}
        isLoading={submissionsList.isLoading}
        useAction={false}
        useDownloadDropdown
      >
        {submissionsList.isSuccess &&
          submissionsList.data &&
          submissionsList.data.data?.map((table, index) => (
            <TableRow key={table.id}>
              <TableCell>{numbers[index] + 1}</TableCell>
              {columns.map((col) => (
                <TableCell key={col.key}>
                  {col.render
                    ? col.render(table as TradeInHistory)
                    : table[col.key as keyof TradeInHistory] || '-'}
                </TableCell>
              ))}
            </TableRow>
          ))}
      </Content>

      <Notification
        open={submissionsList.isError}
        onClose={() => {
          submissionsList.reset()
        }}
        isError={submissionsList.isError}
        message={GLOBAL.returnExceptionMessage(
          submissionsList.isError,
          submissionsList.error as ErrorProps
        )}
      />
    </>
  )
}

export default TradeInList
